import type { StalePrice } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useDesktop, useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

type SortKey = 'risk' | 'name' | 'tier' | 'price' | 'since' | 'then' | 'now' | 'change' | 'gm' | 'floor' | 'sold';
const THRESHOLDS = [5, 8, 15];

/**
 * Prices left behind (decision 111): a published tier price whose cost moved since it was set, or that sits under the
 * floor at today's cost. Under the floor first, then what sells most. Owner and office.
 */
export function StalePricesPage() {
  const { t, intlLocale } = useLocale();
  const desktop = useDesktop();
  const navigate = useNavigate();
  const [raw, setRaw] = useSearchParam('t');
  const threshold = THRESHOLDS.includes(Number(raw)) ? Number(raw) : 8;
  const query = useQuery({ queryKey: qk.stalePrices(threshold), queryFn: ({ signal }) => api.stalePrices(threshold, signal) });
  const sorters = useMemo(
    () => ({
      name: byText<StalePrice>((r) => r.itemName),
      tier: byText<StalePrice>((r) => r.tierCode),
      price: byNumber<StalePrice>((r) => r.price),
      since: byText<StalePrice>((r) => r.priceSince),
      then: byNumber<StalePrice>((r) => r.costThen),
      now: byNumber<StalePrice>((r) => r.costNow),
      change: byNumber<StalePrice>((r) => r.changePct),
      gm: byNumber<StalePrice>((r) => r.marginNowPct),
      // under the floor first, then what sells most (the server's order)
      risk: byNumber<StalePrice>((r) => (r.underFloor ? 1e12 : 0) + r.sold30),
      floor: byNumber<StalePrice>((r) => r.floorPrice),
      sold: byNumber<StalePrice>((r) => r.sold30),
    }),
    [],
  );
  const { sorted, head } = useSort<StalePrice, SortKey>(sorters, { key: 'risk', dir: -1 }, ['risk', 'price', 'then', 'now', 'change', 'gm', 'floor', 'sold']);
  const money = (v: number | null) => (v === null ? '—' : formatMoney(v, intlLocale));
  const pct = (v: number | null) => (v === null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
  const columns: SheetColumn<StalePrice>[] = [
    { key: 'name', header: head('name', t('itemCol')), label: t('itemCol'), width: '220px', render: (r) => <Link to={`/items/${r.itemId}`}>{r.itemName}</Link> },
    { key: 'tier', header: head('tier', t('tierCol')), label: t('tierCol'), width: '100px', render: (r) => r.tierCode },
    { key: 'price', header: head('price', t('priceCol')), label: t('priceCol'), width: '92px', align: 'right', render: (r) => money(r.price) },
    { key: 'since', header: head('since', t('priceSinceCol')), label: t('priceSinceCol'), width: '104px', render: (r) => formatDate(r.priceSince, intlLocale, 'date') },
    { key: 'then', header: head('then', t('costThenCol')), label: t('costThenCol'), width: '92px', align: 'right', render: (r) => money(r.costThen) },
    { key: 'now', header: head('now', t('costNowCol')), label: t('costNowCol'), width: '92px', align: 'right', render: (r) => money(r.costNow) },
    { key: 'change', header: head('change', t('costChangeCol')), label: t('costChangeCol'), width: '84px', align: 'right', render: (r) => pct(r.changePct), cellClass: (r) => ((r.changePct ?? 0) > 0 ? 'warn' : undefined) },
    { key: 'gm', header: head('gm', t('gmNowCol')), label: t('gmNowCol'), width: '84px', align: 'right', render: (r) => `${r.marginNowPct.toFixed(1)}%`, cellClass: (r) => (r.underFloor ? 'bad' : undefined) },
    { key: 'floor', header: head('floor', t('floorCol')), label: t('floorCol'), width: '92px', align: 'right', render: (r) => (r.underFloor ? <strong>{money(r.floorPrice)}</strong> : money(r.floorPrice)), cellClass: (r) => (r.underFloor ? 'bad' : undefined) },
    { key: 'sold', header: head('sold', t('sold30Col')), label: t('sold30Col'), width: '100px', align: 'right', render: (r) => money(r.sold30) },
  ];
  const chips = (
    <div className="chips" role="group" aria-label={t('stalePricesThreshold')}>
      {THRESHOLDS.map((n) => (
        <button key={n} type="button" className="chip" aria-pressed={threshold === n} onClick={() => setRaw(String(n))}>
          ±{n}%
        </button>
      ))}
    </div>
  );
  return (
    <div className={desktop ? 'desk-page' : 'stack'}>
      {desktop ? (
        <header className="desk-head">
          <div>
            <h1>{t('stalePricesTitle')}</h1>
            <p className="muted">{t('stalePricesHint')}</p>
          </div>
        </header>
      ) : (
        <>
          <PageHead title={t('stalePricesTitle')} back="/" />
          <p className="muted">{t('stalePricesHint')}</p>
        </>
      )}
      <div className="toolbar">
        {chips}
        <Link to="/prices" className="btn btn-secondary">
          {t('openPriceManagement')}
        </Link>
      </div>
      <QueryView query={query}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noStalePrices')}</Empty>
          ) : desktop ? (
            <Sheet label={t('stalePricesTitle')} columns={columns} rows={sorted(rows)} rowKey={(r) => `${r.itemId}-${r.tierCode}`} rowLabel={(r) => r.itemName} minWidth="1060px" onOpen={(r) => navigate(`/items/${r.itemId}`)} />
          ) : (
            <ul className="list" data-testid="stale-list">
              {rows.map((r) => (
                <li key={`${r.itemId}-${r.tierCode}`}>
                  <Link className="row" to={`/items/${r.itemId}`}>
                    <span className="row-main">
                      <span className="row-title">
                        {r.itemName} · {r.tierCode}
                        {r.underFloor ? <span className="badge badge-danger">{t('underFloorBadge')}</span> : null}
                      </span>
                      <span className="row-sub">
                        {t('costThenCol')} {money(r.costThen)} → {money(r.costNow)} ({pct(r.changePct)}) · {t('gmNowCol')} {r.marginNowPct.toFixed(1)}%
                      </span>
                    </span>
                    <span className="row-end-value">{money(r.price)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}
